import { Vector2 } from '../types/Vector2';
import * as THREE from 'three';
import { VideoMaterial } from '../materials/VideoMaterial';

type DoubleClickCallback = (position: Vector2) => void;
type ClickCallback = (position: Vector2) => void;

export class InputEventManager {
    // --- Singleton Pattern ---
    private static _instance: InputEventManager;
    public static get Instance(): InputEventManager {
        if (!InputEventManager._instance) {
            InputEventManager._instance = new InputEventManager();
        }
        return InputEventManager._instance;
    }
    
    // --- Public State Properties ---
    public readonly mousePosition: Vector2 = new Vector2();
    public readonly mouseDelta: Vector2 = new Vector2();
    public readonly pressedKeys: Set<string> = new Set();
    public isMouseDown: boolean = false;
    public mouseWheelDelta: number = 0;
    
    // --- Touch State Properties ---
    public readonly touchPosition: Vector2 = new Vector2();
    public readonly touchDelta: Vector2 = new Vector2();
    public isTouchDown: boolean = false;
    public touchCount: number = 0;

    // --- Private State ---
    private _lastMousePosition: Vector2 = new Vector2();
    private _lastTouchPosition: Vector2 = new Vector2();
    private _doubleClickCallbacks: DoubleClickCallback[] = [];
    private _clickCallbacks: ClickCallback[] = [];
    private _canvasElement: HTMLCanvasElement | null = null;
    private _scene: THREE.Scene | null = null;
    private _camera: THREE.Camera | null = null;
    private _raycaster: THREE.Raycaster = new THREE.Raycaster();
    
    // --- Bound Event Handlers ---
    private readonly _boundOnMouseMove: (event: MouseEvent) => void;
    private readonly _boundOnMouseDown: (event: MouseEvent) => void;
    private readonly _boundOnMouseUp: (event: MouseEvent) => void;
    private readonly _boundOnClick: (event: MouseEvent) => void;
    private readonly _boundOnDoubleClick: (event: MouseEvent) => void;
    private readonly _boundOnWheel: (event: WheelEvent) => void;
    private readonly _boundOnKeyDown: (event: KeyboardEvent) => void;
    private readonly _boundOnKeyUp: (event: KeyboardEvent) => void;
    
    // --- Touch Event Handlers ---
    private readonly _boundOnTouchStart: (event: TouchEvent) => void;
    private readonly _boundOnTouchMove: (event: TouchEvent) => void;
    private readonly _boundOnTouchEnd: (event: TouchEvent) => void;
    
    private constructor() {
        if (InputEventManager._instance) {
            throw new Error("Error: Instantiation failed: Use InputEventManager.Instance instead of new.");
        }
        // Bind 'this' to ensure correct context within event handlers
        this._boundOnMouseMove = this._onMouseMove.bind(this);
        this._boundOnMouseDown = this._onMouseDown.bind(this);
        this._boundOnMouseUp = this._onMouseUp.bind(this);
        this._boundOnClick = this._onClick.bind(this);
        this._boundOnDoubleClick = this._onDoubleClick.bind(this);
        this._boundOnWheel = this._onWheel.bind(this);
        this._boundOnKeyDown = this._onKeyDown.bind(this);
        this._boundOnKeyUp = this._onKeyUp.bind(this);
        
        // Bind touch event handlers
        this._boundOnTouchStart = this._onTouchStart.bind(this);
        this._boundOnTouchMove = this._onTouchMove.bind(this);
        this._boundOnTouchEnd = this._onTouchEnd.bind(this);
        
        this._addEventListeners();
    }

    /**
     * This method should be called once per frame, before any input-dependent logic.
     */
    public update(): void {
        // Calculate mouse delta
        this.mouseDelta.x = this.mousePosition.x - this._lastMousePosition.x;
        this.mouseDelta.y = this.mousePosition.y - this._lastMousePosition.y;

        // Calculate touch delta
        this.touchDelta.x = this.touchPosition.x - this._lastTouchPosition.x;
        this.touchDelta.y = this.touchPosition.y - this._lastTouchPosition.y;

        // Update last positions for the next frame
        this._lastMousePosition.copy(this.mousePosition);
        this._lastTouchPosition.copy(this.touchPosition);

        // Reset per-frame values
        this.mouseWheelDelta = 0;
    }

    /**
     * Set the 3D canvas element to listen for mouse events on
     */
    public setCanvas(canvas: HTMLCanvasElement | null): void {
        if (this._canvasElement) {
            // Remove old listeners
            this._removeCanvasEventListeners();
        }
        
        this._canvasElement = canvas;
        
        if (this._canvasElement) {
            // Add new listeners
            this._addCanvasEventListeners();
        }
    }

    /**
     * Set the THREE.Scene for raycasting
     */
    public setScene(scene: THREE.Scene | null): void {
        this._scene = scene;
    }

    /**
     * Set the THREE.Camera for raycasting
     */
    public setCamera(camera: THREE.Camera | null): void {
        this._camera = camera;
    }

    public onDoubleClick(callback: DoubleClickCallback): () => void {
        this._doubleClickCallbacks.push(callback);
        // Return an unsubscribe function
        return () => {
            this._doubleClickCallbacks = this._doubleClickCallbacks.filter(cb => cb !== callback);
        };
    }

    public onClick(callback: ClickCallback): () => void {
        this._clickCallbacks.push(callback);
        // Return an unsubscribe function
        return () => {
            this._clickCallbacks = this._clickCallbacks.filter(cb => cb !== callback);
        };
    }

    // --- Event Handlers ---
    private _onMouseMove(event: MouseEvent): void {
        this.mousePosition.set(event.clientX, event.clientY);
    }
    
    private _onMouseDown(event: MouseEvent): void {
        this.isMouseDown = true;
    }

    private _onMouseUp(event: MouseEvent): void {
        this.isMouseDown = false;
    }

    private _onClick(event: MouseEvent): void {
        if (!this._canvasElement || !this._scene || !this._camera) return;
        
        // Calculate normalized device coordinates
        const rect = this._canvasElement.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        // Set up raycaster
        this._raycaster.setFromCamera(new THREE.Vector2(x, y), this._camera);
        
        // Get intersections
        const intersects = this._raycaster.intersectObjects(this._scene.children, true);
        
        if (intersects.length > 0) {
            const intersect = intersects[0];
            const hit = intersect.object as THREE.Object3D;

            // Walk up the parent chain to find an attached sceneNode
            let obj: THREE.Object3D | null = hit;
            let handled = false;
            let probedAny = false;
            while (obj) {
                const sn = (obj as any).userData?.sceneNode;
                if (sn) {
                    probedAny = true;
                    const mat = sn.getPrimaryMaterial?.() ?? sn.primaryMaterial ?? (sn as any).material;
                    try {
                        const Logger = (window as any)._engineLogger;
                        if (Logger) Logger.Info(`click: sceneNode=${sn.constructor?.name || '?'} mat=${mat?.constructor?.name || 'none'} hasToggle=${typeof mat?.togglePlayPause === 'function'}`, 'Click');
                    } catch {}
                    if (mat && typeof mat.togglePlayPause === 'function') {
                        (mat as any).togglePlayPause();
                        handled = true;
                    }
                    break;
                }
                obj = obj.parent;
            }
            if (!probedAny) {
                try {
                    const Logger = (window as any)._engineLogger;
                    if (Logger) Logger.Info(`click: hit ${hit.constructor?.name || '?'} (no sceneNode in parent chain)`, 'Click');
                } catch {}
            }

            // If a video toggle was handled, consume the click to avoid selection conflicts
            if (handled) {
                return;
            }
        }
        
        // Also trigger generic click callbacks
        const position = new Vector2(event.clientX, event.clientY);
        this._clickCallbacks.forEach(cb => cb(position));
    }

    private _onDoubleClick(event: MouseEvent): void {
        const position = new Vector2(event.clientX, event.clientY);
        this._doubleClickCallbacks.forEach(cb => cb(position));
    }
    
    private _onWheel(event: WheelEvent): void {
        this.mouseWheelDelta = event.deltaY;
    }
    
    // ==================== TOUCH EVENT HANDLERS ====================
    
    private _onTouchStart(event: TouchEvent): void {
        event.preventDefault(); // Prevent scrolling/zooming
        
        if (event.touches.length === 1) {
            // Single touch - treat like mouse down for orbit controls
            this.isTouchDown = true;
            this.touchCount = event.touches.length;
            
            const touch = event.touches[0];
            const rect = this._canvasElement?.getBoundingClientRect();
            if (rect) {
                this.touchPosition.x = touch.clientX - rect.left;
                this.touchPosition.y = touch.clientY - rect.top;
                this._lastTouchPosition.copy(this.touchPosition);
            }
        }
    }
    
    private _onTouchMove(event: TouchEvent): void {
        event.preventDefault(); // Prevent scrolling/zooming
        
        if (event.touches.length === 1 && this.isTouchDown) {
            // Single touch drag - update touch position for orbit controls
            const touch = event.touches[0];
            const rect = this._canvasElement?.getBoundingClientRect();
            if (rect) {
                this.touchPosition.x = touch.clientX - rect.left;
                this.touchPosition.y = touch.clientY - rect.top;
            }
        }
    }
    
    private _onTouchEnd(event: TouchEvent): void {
        event.preventDefault();
        
        this.isTouchDown = false;
        this.touchCount = event.touches.length;
        
        // Reset touch deltas when touch ends
        this.touchDelta.x = 0;
        this.touchDelta.y = 0;
    }
    
    private _onKeyDown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement;
        const isInputFocused = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

        // Allow F12 for dev tools, and other browser shortcuts like F5, Ctrl+R
        if (event.key === 'F12' || event.key === 'F5' || (event.ctrlKey && event.key.toLowerCase() === 'r')) {
            return;
        }
        
        // For keys we want to control (like arrows for camera), we can prevent default,
        // but not if the user is typing in an input field.
        const keysToPrevent = ['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd'];
        if (!isInputFocused && keysToPrevent.includes(event.key.toLowerCase())) {
            event.preventDefault();
        }
        
        this.pressedKeys.add(event.key.toLowerCase());
    }

    private _onKeyUp(event: KeyboardEvent): void {
        this.pressedKeys.delete(event.key.toLowerCase());
    }
    
    private _addEventListeners(): void {
        // Only add keyboard listeners globally - mouse events will be canvas-specific
        window.addEventListener('keydown', this._boundOnKeyDown, false);
        window.addEventListener('keyup', this._boundOnKeyUp, false);
    }

    private _addCanvasEventListeners(): void {
        if (!this._canvasElement) return;
        
        // Add mouse event listeners specifically to the canvas
        this._canvasElement.addEventListener('mousemove', this._boundOnMouseMove, false);
        this._canvasElement.addEventListener('mousedown', this._boundOnMouseDown, false);
        this._canvasElement.addEventListener('mouseup', this._boundOnMouseUp, false);
        this._canvasElement.addEventListener('click', this._boundOnClick, false);
        this._canvasElement.addEventListener('dblclick', this._boundOnDoubleClick, false);
        this._canvasElement.addEventListener('wheel', this._boundOnWheel, false);
        
        // Add touch event listeners for mobile support
        this._canvasElement.addEventListener('touchstart', this._boundOnTouchStart, { passive: false });
        this._canvasElement.addEventListener('touchmove', this._boundOnTouchMove, { passive: false });
        this._canvasElement.addEventListener('touchend', this._boundOnTouchEnd, { passive: false });
    }

    private _removeCanvasEventListeners(): void {
        if (!this._canvasElement) return;
        
        // Remove mouse event listeners from the canvas
        this._canvasElement.removeEventListener('mousemove', this._boundOnMouseMove);
        this._canvasElement.removeEventListener('mousedown', this._boundOnMouseDown);
        this._canvasElement.removeEventListener('mouseup', this._boundOnMouseUp);
        this._canvasElement.removeEventListener('click', this._boundOnClick);
        this._canvasElement.removeEventListener('dblclick', this._boundOnDoubleClick);
        this._canvasElement.removeEventListener('wheel', this._boundOnWheel);
        
        // Remove touch event listeners from the canvas
        this._canvasElement.removeEventListener('touchstart', this._boundOnTouchStart);
        this._canvasElement.removeEventListener('touchmove', this._boundOnTouchMove);
        this._canvasElement.removeEventListener('touchend', this._boundOnTouchEnd);
    }
    
    // Cleanup method if the engine ever needs to shut down
    public dispose(): void {
        // Remove canvas-specific listeners
        this._removeCanvasEventListeners();
        
        // Remove global listeners
        window.removeEventListener('keydown', this._boundOnKeyDown);
        window.removeEventListener('keyup', this._boundOnKeyUp);
    }
} 